import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  Timer, CalendarClock, Droplet, UserCheck, Pencil, BarChart3, UserPlus,
  ShieldCheck, Ban, MapPin, Wallet, Trash2, ChevronRight,
} from "lucide-react";
import { updateJoinRequestSetting } from "../../services/eventDetailService";
import Card from "../common/Card";
import Switch from "../common/Switch";
import { color, radius } from "../../styles/theme";

/**
 * Event Settings hub — ported from mobile's setting-event screen
 * (src/Screens/setting-event), consolidating everything else this panel has
 * already built for an event (QR buffer time, promoter watermark, media
 * history, blocked users, BA permissions, wallet, delete) into one tab,
 * plus the two genuinely new pieces: Watermark Event Details and the
 * Allow Join Requests toggle.
 *
 * Deliberately left out vs. mobile's hub (all non-owner or native-only
 * flows, consistent with this panel's promoter/owner-only role model — see
 * eventPermissions.js): ODP Editing, Notification Permissions for Event
 * (native push), View Authorities (a non-admin assistant's self-view),
 * Leave Room / Leave as Business Assistant.
 */
const SettingsTab = ({
  event,
  eventId,
  permissions,
  onOpenQRBufferTime,
  onOpenWatermarkEventDetails,
  onOpenPromoterWatermark,
  onOpenInviteBA,
  onGoToParticipants,
  onOpenBAPermissions,
  onDelete,
  deleting,
}) => {
  const { t } = useTranslation("eventDetails");
  const navigate = useNavigate();
  const { isAdmin, isFamilyEvent, isARoomEvent, isNonPublicEvent, isPrivateEvent } = permissions;

  const [allowJoinRequests, setAllowJoinRequests] = useState(!event?.IsJoinRequestDisabled);
  const [savingJoinRequests, setSavingJoinRequests] = useState(false);

  const canToggleJoinRequests = isAdmin && !isFamilyEvent && !isARoomEvent;
  const canInviteBA = isAdmin && !isFamilyEvent && !isARoomEvent && !isNonPublicEvent && !isPrivateEvent;

  const handleToggleJoinRequests = async () => {
    const next = !allowJoinRequests;
    setSavingJoinRequests(true);
    try {
      const res = await updateJoinRequestSetting(eventId, !next);
      toast.success(res.data?.Message || t("settingsTab.joinRequestUpdateSuccess"));
      setAllowJoinRequests(next);
    } catch (err) {
      toast.error(err.response?.data?.Message || t("settingsTab.joinRequestUpdateError"));
    } finally {
      setSavingJoinRequests(false);
    }
  };

  if (!isAdmin) {
    return <p style={s.emptyText}>{t("settingsTab.ownerOnly")}</p>;
  }

  return (
    <div>
      <SectionLabel>{t("settingsTab.sections.eventSettings")}</SectionLabel>
      <Card style={s.card}>
        {!isFamilyEvent && !isARoomEvent && (
          <SettingsRow icon={Timer} title={t("settingsTab.rows.qrBufferTime.title")} description={t("settingsTab.rows.qrBufferTime.description")} onClick={onOpenQRBufferTime} />
        )}
        <SettingsRow icon={CalendarClock} title={t("settingsTab.rows.watermarkEventDetails.title")} description={t("settingsTab.rows.watermarkEventDetails.description")} onClick={onOpenWatermarkEventDetails} />
        <SettingsRow icon={Droplet} title={t("settingsTab.rows.promoterWatermark.title")} description={t("settingsTab.rows.promoterWatermark.description")} onClick={onOpenPromoterWatermark} last={!canToggleJoinRequests} />
        {canToggleJoinRequests && (
          <div style={s.switchRow}>
            <span style={s.rowIconBadge}><UserCheck size={16} strokeWidth={2} /></span>
            <span style={{ flex: 1 }}>
              <div style={s.rowTitle}>{t("settingsTab.rows.allowJoinRequests.title")}</div>
              <div style={s.rowDesc}>{t("settingsTab.rows.allowJoinRequests.description")}</div>
            </span>
            <Switch checked={allowJoinRequests} onChange={handleToggleJoinRequests} disabled={savingJoinRequests} />
          </div>
        )}
      </Card>

      <SectionLabel>{t("settingsTab.sections.eventManagement")}</SectionLabel>
      <Card style={s.card}>
        <SettingsRow icon={Pencil} title={t("settingsTab.rows.editEvent.title")} description={t("settingsTab.rows.editEvent.description")} onClick={() => navigate(`/events/${eventId}/edit`)} />
        <SettingsRow icon={BarChart3} title={t("settingsTab.rows.mediaHistory.title")} description={t("settingsTab.rows.mediaHistory.description")} onClick={() => navigate(`/events/${eventId}/media-history`)} />
        {canInviteBA && (
          <SettingsRow icon={UserPlus} title={t("settingsTab.rows.inviteBusinessAssistant.title")} description={t("settingsTab.rows.inviteBusinessAssistant.description")} onClick={onOpenInviteBA} />
        )}
        {canInviteBA && (
          <SettingsRow icon={ShieldCheck} title={t("settingsTab.rows.businessAssistantPermissions.title")} description={t("settingsTab.rows.businessAssistantPermissions.description")} onClick={onOpenBAPermissions} />
        )}
        <SettingsRow icon={Ban} title={t("settingsTab.rows.blockedUsers.title")} description={t("settingsTab.rows.blockedUsers.description")} onClick={onGoToParticipants} />
        <SettingsRow icon={MapPin} title={t("settingsTab.rows.locations.title")} description={t("settingsTab.rows.locations.description")} onClick={() => navigate("/locations", { state: { fromEventId: eventId } })} />
        <SettingsRow icon={Wallet} title={t("settingsTab.rows.wallet.title")} description={t("settingsTab.rows.wallet.description")} onClick={() => navigate("/wallet", { state: { fromEventId: eventId } })} last />
      </Card>

      <SectionLabel tone="danger">{t("settingsTab.sections.dangerZone")}</SectionLabel>
      <Card style={{ ...s.card, border: `1px solid ${color.danger50}` }}>
        <button type="button" className="row-link" style={s.dangerRow} onClick={onDelete} disabled={deleting}>
          <span style={{ ...s.rowIconBadge, background: color.danger50, color: color.danger500 }}><Trash2 size={16} strokeWidth={2} /></span>
          <span style={{ flex: 1, textAlign: "left" }}>
            <div style={{ ...s.rowTitle, color: color.danger500 }}>{t("settingsTab.rows.deleteEvent.title")}</div>
            <div style={s.rowDesc}>{t("settingsTab.rows.deleteEvent.description")}</div>
          </span>
          <ChevronRight size={16} color={color.gray400} />
        </button>
      </Card>
    </div>
  );
};

const SectionLabel = ({ children, tone }) => (
  <h4 style={{ ...s.sectionTitle, ...(tone === "danger" ? { color: color.danger500 } : {}) }}>{children}</h4>
);

const SettingsRow = ({ icon: Icon, title, description, onClick, last = false }) => (
  <button type="button" className="row-link" style={{ ...s.row, ...(last ? { borderBottom: "none" } : {}) }} onClick={onClick}>
    <span style={s.rowIconBadge}><Icon size={16} strokeWidth={2} /></span>
    <span style={{ flex: 1, textAlign: "left" }}>
      <div style={s.rowTitle}>{title}</div>
      {description && <div style={s.rowDesc}>{description}</div>}
    </span>
    <ChevronRight size={16} color={color.gray400} />
  </button>
);

const s = {
  emptyText: { color: color.textMuted, fontSize: 13.5, padding: "20px 0" },
  card: { padding: "6px 20px", marginBottom: 22 },
  sectionTitle: { margin: "0 0 10px 2px", fontSize: 12, fontWeight: 700, color: color.textMuted, textTransform: "uppercase", letterSpacing: 0.6 },
  row: {
    display: "flex", alignItems: "center", gap: 13, width: "100%", padding: "13px 0",
    borderBottom: `1px solid ${color.gray100}`,
  },
  switchRow: { display: "flex", alignItems: "center", gap: 13, width: "100%", padding: "13px 0" },
  rowIconBadge: {
    width: 32, height: 32, borderRadius: radius.sm, background: color.gray100, color: color.gray600,
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  rowTitle: { fontSize: 13.5, fontWeight: 600, color: color.textPrimary },
  rowDesc: { fontSize: 11.5, color: color.textMuted, marginTop: 2 },
  dangerRow: { display: "flex", alignItems: "center", gap: 13, width: "100%", padding: "13px 0" },
};

export default SettingsTab;
